// src/shared/components/telephony/TelephonyModal.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./TelephonyModal.module.css";

/**
 * Универсальный контролируемый модал звонка.
 *
 * Поддерживает два “набора” пропсов:
 *
 * 1) Старый:
 *    - isOpen
 *    - onClose
 *    - callerName
 *    - phoneNumber
 *    - statusText
 *    - timerText
 *    - isMuted
 *    - isOnHold
 *    - isRinging
 *    - onToggleMute
 *    - onToggleHold
 *    - onToggleKeypad
 *    - onHangUp
 *    - onAnswer
 *
 * 2) Новый из ContactsPage:
 *    - open
 *    - contact
 *    - phone
 *    - error
 *    - isCalling
 *    - activeCallSid
 *    - isMuted
 *    - isOnHold
 *    - isRecording
 *    - micLevel
 *    - onToggleMute
 *    - onToggleHold
 *    - onToggleRecord (не используется здесь)
 *    - onHangup
 *    - onStartCall
 */
export default function TelephonyModal(props) {
  const { t } = useTranslation();

  const {
    // старые имена
    isOpen,
    onClose,
    callerName,
    phoneNumber,
    statusText,
    timerText,
    isMuted = false,
    isOnHold = false,
    isRinging,
    onToggleMute,
    onToggleHold,
    onToggleKeypad,
    onHangUp,
    onAnswer,

    // новые имена из ContactsPage
    open,
    contact,
    phone,
    error,
    isCalling,
    activeCallSid,
    isRecording, // пока не отображаем
    micLevel,
    onHangup,
    onStartCall,
  } = props;

  const visible = typeof isOpen !== "undefined" ? isOpen : open;
  if (!visible) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  const name =
    callerName ||
    contact?.full_name ||
    phone ||
    phoneNumber ||
    t("chat_unknown");

  const number = phoneNumber || phone || "";

  const resolvedStatusText =
    statusText ??
    (error
      ? error
      : activeCallSid
      ? t("telephony_in_call")
      : isCalling
      ? t("telephony_calling")
      : t("telephony_ready_to_call"));

  const ringing =
    typeof isRinging !== "undefined"
      ? isRinging
      : !activeCallSid && !!isCalling;

  const handleHang = onHangUp || onHangup || null;
  const handleAnswer = onAnswer || onStartCall || null;

  const bars = new Array(8).fill(0);
  const hasMicLevel = typeof micLevel === "number";

  return (
    <div
      className={`modal fade show ${styles.telephonyModal}`}
      style={{ display: "block" }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className={styles.callModalContent}>
          <button
            type="button"
            className={styles.callClose}
            onClick={onClose}
            aria-label={t("chat_close")}
          >
            <i className="lni lni-xmark"></i>
          </button>

          <div className={styles.callScreen}>
            <div className={styles.callHeader}>
              <div className={styles.callAvatar}>
                {name?.trim()?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className={styles.callName}>{name}</div>

              {number && (
                <input
                  className={styles.callNumberInput}
                  value={number}
                  readOnly
                />
              )}

              {timerText && (
                <div className={styles.callTimer}>{timerText}</div>
              )}
              {resolvedStatusText && (
                <div className={styles.callQueue}>{resolvedStatusText}</div>
              )}
            </div>

            <div className={styles.voiceWaveContainer}>
              {bars.map((_, idx) => {
                let scaleY = 0.25;
                let opacity = 0.55;

                if (hasMicLevel) {
                  const factor = 0.4 + (idx % 4) * 0.18;
                  scaleY = 0.25 + micLevel * factor;
                  opacity = 0.3 + micLevel * 0.7;
                } else if (ringing) {
                  scaleY = 0.4 + (idx % 3) * 0.3;
                  opacity = 0.9;
                }

                return (
                  <div
                    key={idx}
                    className={styles.voiceWaveBar}
                    style={{
                      transform: `scaleY(${scaleY})`,
                      opacity,
                    }}
                  />
                );
              })}
            </div>

            <div className={styles.callActionsGrid}>
              <button
                type="button"
                className={styles.callIconBtn}
                onClick={onToggleMute}
                disabled={!onToggleMute}
              >
                <div className={styles.callIcon}>
                  {isMuted ? (
                    <i className="lni lni-volume-mute"></i>
                  ) : (
                    <i className="lni lni-microphone-1"></i>
                  )}
                </div>
                <span>{isMuted ? t("telephony_unmute") : t("telephony_mute")}</span>
              </button>

              <button
                type="button"
                className={styles.callIconBtn}
                onClick={onToggleHold}
                disabled={!onToggleHold}
              >
                <div className={styles.callIcon}>
                  {isOnHold ? (
                    <i className="lni lni-play"></i>
                  ) : (
                    <i className="lni lni-pause"></i>
                  )}
                </div>
                <span>{isOnHold ? t("telephony_resume") : t("telephony_hold")}</span>
              </button>

              <button
                type="button"
                className={styles.callIconBtn}
                onClick={onToggleKeypad}
                disabled={!onToggleKeypad}
              >
                <div className={styles.callIcon}>
                  <i className="lni lni-calculator-2"></i>
                </div>
                <span>{t("telephony_keypad")}</span>
              </button>
            </div>

            <div className={styles.callFooter}>
              {handleAnswer && (
                <button
                  type="button"
                  className={`${styles.callHangBtn} ${styles.callHangBtnStart}`}
                  onClick={handleAnswer}
                  aria-label={t("telephony_answer")}
                >
                  <i className="lni lni-telephone-1"></i>
                </button>
              )}
              {handleHang && (
                <button
                  type="button"
                  className={`${styles.callHangBtn} ${styles.callHangBtnEnd}`}
                  onClick={handleHang}
                  aria-label={t("telephony_hang_up")}
                >
                  <i className="lni lni-xmark"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}